import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Notification {
  id: string;
  type: 'success' | 'info' | 'warning' | 'error';
  title: string;
  message: string;
  time: string;
  read: boolean;
  requestId?: string;
}

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const navigate = useNavigate();

  useEffect(() => {
    // محاكاة البيانات
    const mockNotifications: Notification[] = [
      {
        id: '1',
        type: 'success',
        title: 'تم إنجاز الطلب',
        message: 'تم تسليم الحاوية الجديدة بنجاح في شارع الملك فهد',
        time: '2024-01-17T10:30:00',
        read: false,
        requestId: '1'
      },
      {
        id: '2',
        type: 'info',
        title: 'طلب قيد التنفيذ',
        message: 'السائق في الطريق لموقعك لتبديل الحاوية',
        time: '2024-01-17T09:15:00',
        read: false,
        requestId: '2'
      },
      {
        id: '3',
        type: 'warning',
        title: 'تأكيد البيانات مطلوب',
        message: 'يرجى التأكد من صحة رقم الحاوية المدخل',
        time: '2024-01-16T16:45:00',
        read: true,
        requestId: '3'
      },
      {
        id: '4',
        type: 'success',
        title: 'تم استلام الطلب',
        message: 'تم استلام طلب الرفع النهائي وسيتم التنفيذ قريباً',
        time: '2024-01-16T14:20:00',
        read: true,
        requestId: '4'
      },
      {
        id: '5',
        type: 'info',
        title: 'تذكير موعد الخدمة',
        message: 'موعد تبديل الحاوية المجدول غداً الساعة 10:00 صباحاً',
        time: '2024-01-15T18:00:00',
        read: true
      },
      {
        id: '6',
        type: 'error',
        title: 'خطأ في الموقع',
        message: 'لم يتم العثور على الموقع المحدد، يرجى التحقق من العنوان',
        time: '2024-01-15T12:30:00',
        read: true,
        requestId: '5'
      }
    ];
    setNotifications(mockNotifications);
  }, []);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success': return 'ri-check-line';
      case 'info': return 'ri-information-line';
      case 'warning': return 'ri-alert-line';
      case 'error': return 'ri-error-warning-line';
      default: return 'ri-notification-line';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'success': return 'from-green-500 to-green-600';
      case 'info': return 'from-blue-500 to-blue-600';
      case 'warning': return 'from-yellow-500 to-yellow-600';
      case 'error': return 'from-red-500 to-red-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getNotificationBg = (type: string) => {
    switch (type) {
      case 'success': return 'bg-green-50';
      case 'info': return 'bg-blue-50';
      case 'warning': return 'bg-yellow-50';
      case 'error': return 'bg-red-50';
      default: return 'bg-gray-50';
    }
  };

  const formatTime = (timeString: string) => {
    const date = new Date(timeString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'منذ قليل';
    } else if (diffInHours < 24) {
      return `منذ ${diffInHours} ساعة`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `منذ ${diffInDays} يوم`;
    }
  };

  const markAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === id ? { ...notif, read: true } : notif
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, read: true }))
    );
  };

  const filteredNotifications = notifications.filter(notif => 
    filter === 'all' || !notif.read
  );

  const unreadCount = notifications.filter(notif => !notif.read).length;

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white p-4 fixed w-full top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center ml-3 hover:bg-white/30 transition-colors"
            >
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
            <div>
              <h1 className="font-bold text-lg">التنبيهات</h1>
              <p className="text-sm opacity-90">
                {unreadCount > 0 ? `${unreadCount} إشعار جديد` : 'جميع الإشعارات مقروءة'}
              </p>
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-sm bg-white/20 px-3 py-1 rounded-full hover:bg-white/30 transition-colors"
            >
              قراءة الكل
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="pt-20 pb-20 px-4">
        {/* Filter Tabs */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              جميع الإشعارات ({notifications.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                filter === 'unread'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              غير مقروءة ({unreadCount})
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="space-y-4">
          {filteredNotifications.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-notification-line text-gray-400 text-2xl"></i>
              </div>
              <h3 className="font-bold text-gray-800 mb-2">لا توجد إشعارات</h3>
              <p className="text-gray-600 text-sm">
                {filter === 'unread' ? 'جميع الإشعارات مقروءة' : 'لم يتم العثور على إشعارات'}
              </p>
            </div>
          ) : (
            filteredNotifications.map((notification) => (
              <div 
                key={notification.id} 
                className={`bg-white rounded-2xl shadow-sm p-6 ${!notification.read ? 'border-r-4 border-green-500' : ''}`}
                onClick={() => !notification.read && markAsRead(notification.id)}
              >
                <div className="flex items-start">
                  <div className={`w-12 h-12 bg-gradient-to-r ${getNotificationColor(notification.type)} rounded-xl flex items-center justify-center ml-4 flex-shrink-0`}>
                    <i className={`${getNotificationIcon(notification.type)} text-white text-xl`}></i>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className={`font-bold text-gray-800 ${!notification.read ? 'text-green-600' : ''}`}>
                        {notification.title}
                      </h3>
                      {!notification.read && (
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      )}
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-3 leading-relaxed">
                      {notification.message}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-gray-500">
                        <i className="ri-time-line ml-1"></i>
                        <span>{formatTime(notification.time)}</span>
                      </div>
                      
                      {notification.requestId && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/requests');
                          }}
                          className="text-xs text-green-600 hover:text-green-700 font-medium"
                        >
                          عرض الطلب
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Notification Settings */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mt-6">
          <h3 className="font-bold text-gray-800 mb-4 text-right">إعدادات الإشعارات</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 text-right">
                <div className="font-medium text-gray-800">إشعارات الطلبات</div>
                <div className="text-sm text-gray-600">تلقي إشعارات عند تحديث حالة الطلبات</div>
              </div>
              <div className="relative">
                <input type="checkbox" defaultChecked className="sr-only" />
                <div className="w-12 h-6 bg-green-500 rounded-full shadow-inner"></div>
                <div className="absolute w-4 h-4 bg-white rounded-full shadow left-1 top-1 transition"></div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex-1 text-right">
                <div className="font-medium text-gray-800">إشعارات التذكير</div>
                <div className="text-sm text-gray-600">تذكيرات بمواعيد الخدمة</div>
              </div>
              <div className="relative">
                <input type="checkbox" defaultChecked className="sr-only" />
                <div className="w-12 h-6 bg-green-500 rounded-full shadow-inner"></div>
                <div className="absolute w-4 h-4 bg-white rounded-full shadow left-1 top-1 transition"></div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex-1 text-right">
                <div className="font-medium text-gray-800">إشعارات الأخطاء</div>
                <div className="text-sm text-gray-600">تنبيهات عند حدوث مشاكل</div>
              </div>
              <div className="relative">
                <input type="checkbox" defaultChecked className="sr-only" />
                <div className="w-12 h-6 bg-green-500 rounded-full shadow-inner"></div>
                <div className="absolute w-4 h-4 bg-white rounded-full shadow left-1 top-1 transition"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 w-full bg-white border-t border-gray-200 px-4 py-2">
        <div className="grid grid-cols-4 gap-1">
          <button 
            onClick={() => navigate('/')}
            className="flex flex-col items-center py-2 px-1 text-gray-400 hover:text-gray-600"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-home-line text-lg"></i>
            </div>
            <span className="text-xs">الرئيسية</span>
          </button>
          <button 
            onClick={() => navigate('/requests')}
            className="flex flex-col items-center py-2 px-1 text-gray-400 hover:text-gray-600"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-file-list-line text-lg"></i>
            </div>
            <span className="text-xs">طلباتي</span>
          </button>
          <button className="flex flex-col items-center py-2 px-1 text-green-500">
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-notification-fill text-lg"></i>
            </div>
            <span className="text-xs">التنبيهات</span>
          </button>
          <button 
            onClick={() => navigate('/profile')}
            className="flex flex-col items-center py-2 px-1 text-gray-400 hover:text-gray-600"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-user-line text-lg"></i>
            </div>
            <span className="text-xs">الملف الشخصي</span>
          </button>
        </div>
      </div>
    </div>
  );
}